'use client'

import * as React from 'react'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react'
import {
  type CustomComponents as DayPickerComponents,
  DayButton,
  DayPicker,
  getDefaultClassNames,
} from 'react-day-picker'

import { cn } from '@styleguide/lib/utils'
import {
  CalendarButton,
  calendarButtonVariants,
} from '@styleguide/components/ui/calendar-button'
import { Button } from '@styleguide/components/ui/button'

/**
 * @summary Use for picking a date or date range from a month grid, shown inline
 * or opened from a Popover beside a date field.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames()
  const mergedComponents = React.useMemo(
    () => ({ ...CALENDAR_COMPONENTS, ...components }),
    [components],
  )

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'bg-background group/calendar p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn(
          'w-fit [&_table]:!my-0 [&_table]:!mt-0 [&_table]:!mb-0',
          defaultClassNames.root,
        ),
        months: cn(
          'relative flex flex-col gap-4 md:flex-row',
          defaultClassNames.months,
        ),
        month: cn('flex w-full flex-col gap-2', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          calendarButtonVariants({ variant: 'ghost', size: 'sm' }),
          'flex aspect-square min-w-[--cell-size] font-normal leading-none rounded-md select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          calendarButtonVariants({ variant: 'ghost', size: 'sm' }),
          'flex aspect-square min-w-[--cell-size] font-normal leading-none rounded-md select-none aria-disabled:opacity-50',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-md border',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          'bg-popover absolute inset-0 opacity-0',
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : '[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table:
          'w-full border-collapse [&_td]:!border-0 [&_th]:!border-0 [&_td]:!border-b-0 [&_th]:!border-b-0 !my-0 !mt-0 !mb-0',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-muted-foreground flex-1 select-none rounded-md text-[0.8rem] !font-normal !border-0 !border-b-0 mx-1 text-center flex items-center justify-center',
          defaultClassNames.weekday,
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn(
          'w-[--cell-size] select-none',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'text-muted-foreground select-none text-[0.8rem]',
          defaultClassNames.week_number,
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full select-none !p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md !border-0 !border-b-0 mx-1',
          defaultClassNames.day,
        ),
        range_start: cn(
          'bg-gray-800 rounded-l-md',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('bg-gray-800 rounded-r-md', defaultClassNames.range_end),
        today: cn(
          'bg-gray-600 text-white rounded-md data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: cn(
          'text-muted-foreground/50 aria-selected:text-muted-foreground/50',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={mergedComponents}
      {...props}
    />
  )
}

function CalendarRoot({
  className,
  rootRef,
  ...props
}: React.ComponentProps<DayPickerComponents['Root']>) {
  return (
    <div
      data-slot="calendar"
      ref={rootRef}
      className={cn(className)}
      {...props}
    />
  )
}

function CalendarChevron({
  className,
  orientation,
  ...props
}: React.ComponentProps<DayPickerComponents['Chevron']>) {
  if (orientation === 'left') {
    return <ChevronLeftIcon className={cn('size-4', className)} {...props} />
  }

  if (orientation === 'right') {
    return <ChevronRightIcon className={cn('size-4', className)} {...props} />
  }

  return <ChevronDownIcon className={cn('size-4', className)} {...props} />
}

function CalendarPreviousMonthButton({
  className,
  ...props
}: React.ComponentProps<DayPickerComponents['PreviousMonthButton']>) {
  return (
    <Button
      variant="ghost"
      size="small"
      className={cn('size-8 px-0', className)}
      {...props}
    />
  )
}

function CalendarNextMonthButton({
  className,
  ...props
}: React.ComponentProps<DayPickerComponents['NextMonthButton']>) {
  return (
    <Button
      variant="ghost"
      size="small"
      className={cn('size-8 px-0', className)}
      {...props}
    />
  )
}

function CalendarWeekNumber({
  children,
  ...props
}: React.ComponentProps<DayPickerComponents['WeekNumber']>) {
  return (
    <td {...props}>
      <div className="flex size-[--cell-size] items-center justify-center text-center">
        {children}
      </div>
    </td>
  )
}

// Module-scope so every render passes DayPicker the same component
// identities: an inline override is a new function type on each render, and
// React responds by unmounting and rebuilding the whole day grid. Any click
// already dispatched at a day button then lands on an orphaned node.
const CALENDAR_COMPONENTS = {
  Root: CalendarRoot,
  Chevron: CalendarChevron,
  DayButton: CalendarDayButton,
  PreviousMonthButton: CalendarPreviousMonthButton,
  NextMonthButton: CalendarNextMonthButton,
  WeekNumber: CalendarWeekNumber,
} satisfies Partial<DayPickerComponents>

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <CalendarButton
      ref={ref}
      variant="ghost"
      size="sm"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'data-[selected-single=true]:bg-gray-800 data-[selected-single=true]:text-white data-[range-middle=true]:bg-gray-200 data-[range-middle=true]:text-gray-800 data-[range-start=true]:bg-gray-800 data-[range-start=true]:text-white data-[range-end=true]:bg-gray-800 data-[range-end=true]:text-white group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 flex aspect-square h-9 w-9 flex-col gap-1 font-normal leading-none rounded-md data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] [&>span]:text-xs [&>span]:opacity-70 focus:!ring-gray-300 active:!ring-gray-300',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
